import React, { useMemo, useEffect, useRef } from "react";
import { Button, Label, TextInput, Textarea, Spinner } from "flowbite-react";
import { useForm, Controller } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import Select from "react-select";

import { ISupportTicket, TSupportCategory } from "../../../interfaces";
import { customSelectStyles } from "../../../shared/constants";
import { useAddSupportTicket, useAuth, useIntegrations, useTrackEvent } from "../../../hooks";
import { useTheme } from "../../../providers/ThemeProvider";

const schema = yup.object({
  subject: yup.string().required("Subject is required"),
  category: yup.string().oneOf(["general", "technical", "billing", "feature_request", "bug_report", "account", "other"]).required("Category is required"),
  message: yup.string().required("Message is required"),
  pair_name: yup.string().optional(),
  tracking_id: yup.string().optional(),
}).required();

const categoryOptions: { value: TSupportCategory; label: string }[] = [
  { value: "general", label: "General" },
  { value: "technical", label: "Technical" },
  { value: "feature_request", label: "Feature Request" },
  { value: "bug_report", label: "Bug Report" },
  { value: "account", label: "Account" },
  { value: "other", label: "Other" },
];

const ContactForm: React.FC = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const queryClient = useQueryClient();
  const { mutate: addTicket, isPending } = useAddSupportTicket();
  const { user } = useAuth();
  const { data: integrations } = useIntegrations();

  const { trackEvent } = useTrackEvent();
  const wasSubmitted = useRef(false);

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors, isDirty },
  } = useForm<ISupportTicket>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      category: "general" as TSupportCategory,
      subject: "",
      message: "",
      pair_name: "",
      tracking_id: "",
    }
  });

  const integrationOptions = useMemo(() => {
    if (!integrations) return [];
    return Object.entries(integrations).map(([trackingId, set]) => {
      const label = set.name
      return {
        value: trackingId,
        label: label,
        pair_name: label,
      };
    });
  }, [integrations]);

  useEffect(() => {
    return () => {
      if (isDirty && !wasSubmitted.current) {
        trackEvent("form_abandoned", { form_id: "support_contact" });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = (data: ISupportTicket) => {
    addTicket(data, {
      onSuccess: () => {
        toast.success(t("common.supportTicketCreated", "Support ticket created successfully!"));
        wasSubmitted.current = true;
        trackEvent("form_submitted", {
          form_id: "support_contact",
          category: data.category,
          has_integration: data.tracking_id ? true : false,
          result: "success",
        });
        reset({
          subject: "",
          category: "general",
          message: "",
          pair_name: "",
          tracking_id: "",
        });
        wasSubmitted.current = false;
        queryClient.invalidateQueries({ queryKey: ["support-tickets"] });
      },
      onError: (error: Error) => {
        toast.error(error.message || "Failed to create support ticket");
        trackEvent("error_displayed", {
          error_context: "support_contact_form",
          error_message: error.message ?? "Failed to create support ticket",
        });
      }
    });
  };

  return (
    <div className="rounded-xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-4">
      <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Contact Support</h4>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-3"
      >
        <div className="grid grid-cols-1 gap-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2">
            <div>
              <Label htmlFor="hc_name" value="Name" className="mb-1 block" />
              <TextInput
                id="hc_name"
                value={user?.name || ""}
                readOnly
                disabled
                className="bg-gray-50"
              />
            </div>
            <div>
              <Label htmlFor="hc_email" value="Email" className="mb-1 block" />
              <TextInput
                id="hc_email"
                value={user?.email || ""}
                readOnly
                disabled
                className="bg-gray-50"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="hc_subject" value="Subject" className="mb-1 block" />
            <TextInput
              id="hc_subject"
              placeholder="Brief summary of your issue"
              {...register("subject")}
              color={errors.subject ? "failure" : "gray"}
            />
            <div className="min-h-[20px] mt-1">
              {errors.subject && (
                <p className="text-xs text-red-600">{errors.subject.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="hc_category" value="Category" className="mb-1 block" />
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <Select<{ value: TSupportCategory; label: string }>
                    id="hc_category"
                    options={categoryOptions}
                    styles={customSelectStyles(isDark)}
                    value={categoryOptions.find((opt) => opt.value === field.value) || null}
                    onChange={(val) => field.onChange(val?.value)}
                    classNamePrefix="custom-select"
                    isSearchable={false}
                  />
                )}
              />
              <div className="min-h-[20px] mt-1">
                {errors.category && (
                  <p className="text-xs text-red-600">{errors.category.message}</p>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="hc_integration" value="Integration (Optional)" className="mb-1 block" />
              <Controller
                name="tracking_id"
                control={control}
                render={({ field }) => (
                  <Select
                    id="hc_integration"
                    options={integrationOptions}
                    styles={customSelectStyles(isDark)}
                    value={integrationOptions.find((opt) => opt.value === field.value) || null}
                    onChange={(val: any) => {
                      field.onChange(val?.value || "");
                      setValue("pair_name", val?.pair_name || "");
                    }}
                    classNamePrefix="custom-select"
                    isSearchable={true}
                    placeholder="Select Integration"
                    isClearable={true}
                  />
                )}
              />
              <div className="min-h-[20px] mt-1">
                {errors.tracking_id && (
                  <p className="text-xs text-red-600">{errors.tracking_id.message}</p>
                )}
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="hc_message" value="Message" className="mb-1 block" />
            <Textarea
              id="hc_message"
              rows={6}
              placeholder="Describe the issue you’re facing, steps to reproduce, and any screenshots/links."
              {...register("message")}
              color={errors.message ? "failure" : "gray"}
            />
            <div className="min-h-[20px] mt-1">
              {errors.message && (
                <p className="text-xs text-red-600">{errors.message.message}</p>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2">
          <Button color="light" type="reset" onClick={() => reset()}>Clear</Button>
          <Button
            color="indigo"
            type="submit"
            disabled={isPending}
            isProcessing={isPending}
            processingSpinner={<Spinner color="white" light size="sm" />}
          >
            Send message
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;
